import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ConnectionLineType,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import CustomNode from './CustomNode';
import CustomEdge from './CustomEdge';
import './FlowCanvas.css';

const nodeTypes = {
  custom: CustomNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

const initialNodes = [
  {
    id: 'node-1',
    type: 'custom',
    position: { x: 100, y: 100 },
    data: {
      label: 'Start',
      icon: '🚀',
      description: 'Workflow starting point',
      category: 'trigger',
      hasOutput: true,
      hasInput: false,
    },
  },
  {
    id: 'node-2',
    type: 'custom',
    position: { x: 300, y: 200 },
    data: {
      label: 'Process Data',
      icon: '⚙️',
      description: 'Process the input data',
      category: 'action',
      hasOutput: true,
      hasInput: true,
    },
  },
  {
    id: 'node-3',
    type: 'custom',
    position: { x: 500, y: 100 },
    data: {
      label: 'End',
      icon: '🎯',
      description: 'Workflow endpoint',
      category: 'logic',
      hasOutput: false,
      hasInput: true,
    },
  },
];

const initialEdges = [
  {
    id: 'edge-1',
    source: 'node-1',
    target: 'node-2',
    type: 'custom',
    animated: true,
    style: {
      stroke: '#04c0ff',
      strokeWidth: 2,
    },
    markerEnd: {
      type: 'arrowclosed',
      color: '#04c0ff',
    },
    data: {
      onDelete: (edgeId) => {
        // This will be updated in useEffect
      }
    }
  },
  {
    id: 'edge-2',
    source: 'node-2',
    target: 'node-3',
    type: 'custom',
    animated: true,
    style: {
      stroke: '#04c0ff',
      strokeWidth: 2,
    },
    markerEnd: {
      type: 'arrowclosed',
      color: '#04c0ff',
    },
    data: {
      onDelete: (edgeId) => {
        // This will be updated in useEffect
      }
    }
  },
];

function FlowCanvas({ 
  onNodesChange, 
  onEdgesChange, 
  selectedNode, 
  setSelectedNode,
  isExecuting = false,
  executedNodes = new Set(),
  currentExecutingNode = null
}) {
  const [nodes, setNodes, onNodesChangeHandler] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChangeHandler] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [nodeConfigurations, setNodeConfigurations] = useState(new Map());
  const reactFlowWrapper = useRef(null);

  // Create stable event handlers with useCallback
  const handleNodeEdit = useCallback((nodeId) => {
    setNodes((nds) => {
      const node = nds.find(n => n.id === nodeId);
      if (node && setSelectedNode) {
        setSelectedNode(node);
      }
      return nds;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSelectedNode]);

  const handleNodeDelete = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setNodeConfigurations((configs) => {
      const newConfigs = new Map(configs);
      newConfigs.delete(nodeId);
      return newConfigs;
    });
  }, [setNodes, setEdges, setNodeConfigurations]);

  const handleNodeDuplicate = useCallback((nodeId) => {
    setNodes((nds) => {
      const sourceNode = nds.find(n => n.id === nodeId);
      if (sourceNode) {
        const duplicateNodeId = `node-${Date.now()}-duplicate`;
        
        // Find an optimal position for the duplicate node
        const findOptimalPosition = (originalPos) => {
          const nodeWidth = 200;
          const nodeHeight = 120;
          const spacing = 20;
          
          // Try positions: right, left, below, above
          const candidatePositions = [
            { x: originalPos.x + nodeWidth + spacing, y: originalPos.y }, // Right
            { x: originalPos.x - nodeWidth - spacing, y: originalPos.y }, // Left
            { x: originalPos.x, y: originalPos.y + nodeHeight + spacing }, // Below
            { x: originalPos.x, y: originalPos.y - nodeHeight - spacing }, // Above
            { x: originalPos.x + nodeWidth + spacing, y: originalPos.y + nodeHeight + spacing }, // Bottom-right
            { x: originalPos.x - nodeWidth - spacing, y: originalPos.y - nodeHeight - spacing }, // Top-left
          ];
          
          // Check each candidate position for collisions
          for (const candidatePos of candidatePositions) {
            let hasCollision = false;
            
            for (const existingNode of nds) {
              if (existingNode.id === nodeId) continue; // Skip the source node
              
              const existingPos = existingNode.position;
              
              // Check for overlap (with some padding)
              const overlap = !(
                candidatePos.x + nodeWidth < existingPos.x ||
                candidatePos.x > existingPos.x + nodeWidth ||
                candidatePos.y + nodeHeight < existingPos.y ||
                candidatePos.y > existingPos.y + nodeHeight
              );
              
              if (overlap) {
                hasCollision = true;
                break;
              }
            }
            
            if (!hasCollision) {
              return candidatePos;
            }
          }
          
          // If all positions have collisions, use right position with extra offset
          return {
            x: originalPos.x + nodeWidth + spacing,
            y: originalPos.y
          };
        };
        
        const optimalPosition = findOptimalPosition(sourceNode.position);
        
        const duplicateNode = {
          ...sourceNode,
          id: duplicateNodeId,
          position: optimalPosition,
          data: { 
            ...sourceNode.data,
            onEdit: handleNodeEdit,
            onDelete: handleNodeDelete,
            onDuplicate: handleNodeDuplicate,
          }
        };
        return nds.concat(duplicateNode);
      }
      return nds;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdgeDelete = useCallback((edgeId) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
  }, [setEdges]);

  const onConnect = useCallback(
    (params) => {
      const edge = {
        ...params,
        type: 'custom',
        id: `edge-${params.source}-${params.target}`,
        animated: true,
        style: {
          stroke: '#04c0ff',
          strokeWidth: 2,
        },
        markerEnd: {
          type: 'arrowclosed',
          color: '#04c0ff',
        },
      };
      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges]
  );

  const onNodesChangeLocal = useCallback(
    (changes) => {
      onNodesChangeHandler(changes);
      if (onNodesChange) {
        onNodesChange(changes);
      }
    },
    [onNodesChangeHandler, onNodesChange]
  );

  const onEdgesChangeLocal = useCallback(
    (changes) => {
      onEdgesChangeHandler(changes);
      if (onEdgesChange) {
        onEdgesChange(changes);
      }
    },
    [onEdgesChangeHandler, onEdgesChange]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const nodeData = event.dataTransfer.getData('application/reactflow');
      
      if (!nodeData || !reactFlowInstance) {
        return;
      }

      const parsedData = JSON.parse(nodeData);
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNodeId = `node-${Date.now()}`;
      const newNode = {
        id: newNodeId,
        type: 'custom',
        position,
        data: {
          ...parsedData,
          onEdit: handleNodeEdit,
          onDelete: handleNodeDelete,
          onDuplicate: handleNodeDuplicate,
          configuration: nodeConfigurations.get(newNodeId) || {},
          isExecuting: isExecuting && currentExecutingNode === newNodeId,
          isExecuted: executedNodes.has(newNodeId)
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reactFlowInstance, handleNodeEdit, handleNodeDelete, handleNodeDuplicate, nodeConfigurations, isExecuting, currentExecutingNode, executedNodes]
  );

  const onNodeClick = useCallback((event, node) => {
    if (setSelectedNode) {
      setSelectedNode(node);
    }
  }, [setSelectedNode]);

  const onInit = useCallback((rfi) => {
    setReactFlowInstance(rfi);
  }, []);

  // Function to save node configuration
  const saveNodeConfiguration = useCallback((nodeId, configuration) => {
    setNodeConfigurations((configs) => {
      const newConfigs = new Map(configs);
      newConfigs.set(nodeId, configuration);
      return newConfigs;
    });

    // Update the node data with the new configuration
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, configuration } }
          : node
      )
    );
  }, [setNodes]);

  // Update nodes and edges with stable event handlers - only run once
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onEdit: handleNodeEdit,
          onDelete: handleNodeDelete,
          onDuplicate: handleNodeDuplicate,
        }
      }))
    );

    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          onDelete: handleEdgeDelete
        }
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNodeEdit, handleNodeDelete, handleNodeDuplicate, handleEdgeDelete]);

  // Update nodes when external changes occur
  useEffect(() => {
    if (selectedNode) {
      setNodes((nds) => {
        const existingNode = nds.find(n => n.id === selectedNode.id);
        if (existingNode) {
          return nds.map((node) =>
            node.id === selectedNode.id
              ? { ...node, data: { ...node.data, ...selectedNode.data } }
              : node
          );
        }
        return nds;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode]); // Only depend on selectedNode

  // Update nodes with execution state
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isExecuting: isExecuting && currentExecutingNode === node.id,
          isExecuted: executedNodes.has(node.id)
        }
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExecuting, currentExecutingNode, executedNodes]); // Remove setNodes from dependencies

  // Function to add a node programmatically
  const addNode = useCallback((nodeData) => {
    const newNodeId = `node-${Date.now()}`;
    const position = {
      x: 200 + Math.random() * 300, // Random position in center area
      y: 100 + Math.random() * 200
    };

    const newNode = {
      id: newNodeId,
      type: 'custom',
      position,
      data: {
        ...nodeData,
        onEdit: handleNodeEdit,
        onDelete: handleNodeDelete,
        onDuplicate: handleNodeDuplicate,
        configuration: {},
        isExecuting: false,
        isExecuted: false
      },
    };

    setNodes((nds) => nds.concat(newNode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNodeEdit, handleNodeDelete, handleNodeDuplicate]);

  // Function to focus on a specific node
  const focusNode = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node && reactFlowInstance) {
      // Center the view on the node
      reactFlowInstance.setCenter(node.position.x + 150, node.position.y + 60, { zoom: 1 });
      
      // Select the node
      if (setSelectedNode) {
        setSelectedNode(node);
      }
    }
  }, [nodes, reactFlowInstance, setSelectedNode]);

  // Expose API methods to parent component
  useEffect(() => {
    if (window.flowCanvasAPI) {
      window.flowCanvasAPI.saveNodeConfiguration = saveNodeConfiguration;
      window.flowCanvasAPI.getNodes = () => nodes;
      window.flowCanvasAPI.getEdges = () => edges;
      window.flowCanvasAPI.getNodeConfigurations = () => nodeConfigurations;
      window.flowCanvasAPI.addNode = addNode;
      window.flowCanvasAPI.focusNode = focusNode;
    } else {
      window.flowCanvasAPI = {
        saveNodeConfiguration,
        getNodes: () => nodes,
        getEdges: () => edges,
        getNodeConfigurations: () => nodeConfigurations,
        addNode,
        focusNode
      };
    }
  }, [saveNodeConfiguration, nodes, edges, nodeConfigurations, addNode, focusNode]);

  return (
    <div className="flow-canvas-container">
      <div className="flow-canvas-wrapper" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChangeLocal}
          onEdgesChange={onEdgesChangeLocal}
          onConnect={onConnect}
          onInit={onInit}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          connectionLineType={ConnectionLineType.SmoothStep}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          snapToGrid={true}
          snapGrid={[20, 20]}
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
        >
          <Controls 
            className="flow-controls"
            showInteractive={false}
          />
          <Background 
            variant="dots" 
            gap={20} 
            size={1}
            color="#e1e5e9"
          />
          <Panel position="top-right" className="flow-panel">
            <div className="flow-info">
              <span>{nodes.length} nodes</span>
              <span>{edges.length} connections</span>
            </div>
          </Panel>
        </ReactFlow>
      </div>
    </div>
  );
}

export default function FlowCanvasProvider(props) {
  return (
    <ReactFlowProvider>
      <FlowCanvas {...props} />
    </ReactFlowProvider>
  );
}